import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { gsApiV3 } from "../auth/auth";
import {
  getThunkResponse,
  v3CustomFieldResponses,
  v3Players,
  v3TournamentTeams,
  v3Sponsors,
  v3RegistrationOrders,
  v3Tournaments,
} from "../app/api";
import { getResponse } from "../helpers/JSONapi";

const recordBaseUrls = {
  players: v3Players,
  "tournament-teams": v3TournamentTeams,
  sponsors: v3Sponsors,
  "registration-orders": v3RegistrationOrders,
};

export const getRecordCustomFieldResponses = createAsyncThunk(
  "customFieldResponses/getRecordCustomFieldResponses",
  async ({ recordType, key }, thunkAPI) => {
    const baseUrl = recordBaseUrls[recordType];
    if (!baseUrl) {
      return thunkAPI.rejectWithValue({
        errors: [{ detail: `Unknown record type: ${recordType}` }],
      });
    }
    const response = getResponse(
      gsApiV3.get,
      `${baseUrl}/${key}/custom-field-responses`,
      undefined,
      thunkAPI
    );
    return response;
  }
);

export const getOrderFormResponses = createAsyncThunk(
  "customFieldResponses/getOrderFormResponses",
  async ({ key }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.get,
      `${v3RegistrationOrders}/${key}/form-responses`,
      undefined,
      thunkAPI
    );
    return response;
  }
);

export const getCustomFieldResponsesSummary = createAsyncThunk(
  "customFieldResponses/getCustomFieldResponsesSummary",
  async ({ tournamentKey, questionKey }, thunkAPI) => {
    const filter = questionKey ? { questionKey } : {};
    const response = getResponse(
      gsApiV3.get,
      `${v3Tournaments}/${tournamentKey}/custom-field-responses-summary`,
      { data: filter },
      thunkAPI
    );
    return response;
  }
);

export const attachRecordForms = createAsyncThunk(
  "customFieldResponses/attachRecordForms",
  async ({ recordType, key, registrationFieldSets }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.post,
      `${recordBaseUrls[recordType]}/${key}/custom-field-forms`,
      { registrationFieldSets },
      thunkAPI
    );
    return response;
  }
);

export const removeRecordForm = createAsyncThunk(
  "customFieldResponses/removeRecordForm",
  async ({ recordType, key, fieldSetKey }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.delete,
      `${recordBaseUrls[recordType]}/${key}/custom-field-forms/${fieldSetKey}`,
      { data: {} },
      thunkAPI
    );
    return response;
  }
);

export const createCustomFieldResponse = createAsyncThunk(
  "customFieldResponses/createCustomFieldResponse",
  async (payload, thunkAPI) => {
    const response = getResponse(
      gsApiV3.post,
      v3CustomFieldResponses,
      payload,
      thunkAPI
    );
    return response;
  }
);

export const updateCustomFieldResponse = createAsyncThunk(
  "customFieldResponses/updateCustomFieldResponse",
  async ({ id, value }, thunkAPI) => {
    const response = getResponse(
      gsApiV3.patch,
      `${v3CustomFieldResponses}/${id}`,
      { value },
      thunkAPI
    );
    return response;
  }
);

export const customFieldResponsesSlice = createSlice({
  name: "customFieldResponses",
  initialState: {
    currentRecordResponses: {},
    currentOrderFormResponses: {},
    currentResponsesSummary: [],
    notifications: [],
    loading: [],
  },
  reducers: {
    clearNotifications: (state) => {
      state.notifications = [];
    },
    clearCurrentRecordResponses: (state) => {
      state.currentRecordResponses = {};
    },
    clearCurrentOrderFormResponses: (state) => {
      state.currentOrderFormResponses = {};
    },
    clearCurrentResponsesSummary: (state) => {
      state.currentResponsesSummary = [];
    },
  },
  extraReducers: {
    ...getThunkResponse(getRecordCustomFieldResponses, (state, action) => {
      state.currentRecordResponses = action.payload ?? {};
    }),
    ...getThunkResponse(getOrderFormResponses, (state, action) => {
      state.currentOrderFormResponses = action.payload ?? {};
    }),
    ...getThunkResponse(getCustomFieldResponsesSummary, (state, action) => {
      state.currentResponsesSummary = action.payload ?? [];
    }),
    ...getThunkResponse(attachRecordForms, (state, action) => {
      state.currentRecordResponses = action.payload ?? {};
    }),
    ...getThunkResponse(removeRecordForm, () => {}),
    ...getThunkResponse(createCustomFieldResponse, () => {}),
    ...getThunkResponse(updateCustomFieldResponse, () => {}),
  },
});

export const {
  clearNotifications,
  clearCurrentRecordResponses,
  clearCurrentOrderFormResponses,
  clearCurrentResponsesSummary,
} = customFieldResponsesSlice.actions;

export const selectCurrentRecordResponses = (state) =>
  state?.[customFieldResponsesSlice.name]?.currentRecordResponses;
export const selectCurrentOrderFormResponses = (state) =>
  state?.[customFieldResponsesSlice.name]?.currentOrderFormResponses;
export const selectCurrentResponsesSummary = (state) =>
  state?.[customFieldResponsesSlice.name]?.currentResponsesSummary;
export const selectCustomFieldResponsesNotifications = (state) =>
  state?.[customFieldResponsesSlice.name]?.notifications;
export const selectCustomFieldResponsesLoading = (state) =>
  state?.[customFieldResponsesSlice.name]?.loading;

export default customFieldResponsesSlice.reducer;
